/**
 * Reusable, source-agnostic Google `JobPosting` + `BreadcrumbList` builders.
 *
 * `buildJobPostingSchema(descriptor)` takes an already-normalized descriptor and
 * returns a schema.org JobPosting object (or `null` when required data is
 * missing — never a half-built object). It branches on data completeness and
 * apply type so first-party recruiter jobs can reuse it later (see SEO-NOTES.md
 * "LATER") without a rewrite. For now only walk-in drives call it, via the
 * `walkinToJobPostingDescriptor(drive)` adapter below.
 *
 * Rules honoured:
 *  - Omit any property whose data is missing (no nulls).
 *  - Schema must mirror the visible page (same title / salary / location).
 *  - Strip <script> from description; emit description as HTML.
 */

const SITE = 'https://www.jobxbharat.com';

/* ── helpers ─────────────────────────────────────────────────────────────── */

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi',
];

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Convert plain text (with \n) to safe HTML paragraphs, stripping <script>. */
export function textToHtml(text) {
  if (!text) return '';
  const noScript = String(text).replace(/<script[\s\S]*?<\/script>/gi, '');
  return noScript
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Turn a string[] into an HTML <ul>. */
function listToHtml(items) {
  if (!Array.isArray(items) || items.length === 0) return '';
  return `<ul>${items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
}

const VALID_EMPLOYMENT_TYPES = new Set([
  'FULL_TIME', 'PART_TIME', 'CONTRACTOR', 'TEMPORARY', 'INTERN',
  'VOLUNTEER', 'PER_DIEM', 'OTHER',
]);

/**
 * Normalize a free-text or enum employment type to a valid Google enum.
 * Handles source noise like "Full Time", "Full Time, Permanent", "FULL TIME",
 * "Full time, Permanent" → FULL_TIME. Never returns an invalid enum; defaults
 * to FULL_TIME only when nothing usable is present.
 */
export function mapEmploymentType(raw) {
  if (!raw) return 'FULL_TIME';

  // Already a valid enum? (e.g. descriptor pre-normalized upstream)
  const upper = String(raw).trim().toUpperCase().replace(/[\s-]+/g, '_');
  const firstToken = upper.split(/[,/|]/)[0].replace(/_PERMANENT$/, '');
  if (VALID_EMPLOYMENT_TYPES.has(firstToken)) return firstToken;

  const s = String(raw).toLowerCase();
  if (/part[\s-]?time/.test(s)) return 'PART_TIME';
  if (/contract/.test(s)) return 'CONTRACTOR';
  if (/tempor/.test(s)) return 'TEMPORARY';
  if (/intern/.test(s)) return 'INTERN';
  if (/volunt/.test(s)) return 'VOLUNTEER';
  if (/full[\s-]?time|permanent/.test(s)) return 'FULL_TIME';
  return 'FULL_TIME';
}

/** Parse "₹3 – 5.5 LPA" / "Up to ₹4 LPA" → { min, max } in LPA (numbers). */
export function parseSalaryLpa(display) {
  if (!display) return {};
  const nums = String(display).match(/\d+(?:\.\d+)?/g);
  if (!nums || nums.length === 0) return {};
  if (nums.length === 1) {
    return /up to/i.test(display) ? { max: +nums[0] } : { min: +nums[0] };
  }
  return { min: +nums[0], max: +nums[1] };
}

/**
 * Tolerant parser for legacy walk-in date strings like
 * "24th June – 8th July 2026" or "22nd June – 10th July". Returns
 * { start, end } as ISO strings when parseable, else {}.
 */
export function parseDriveDates(str) {
  if (!str) return {};
  const yearMatch = String(str).match(/\b(20\d{2})\b/);
  const year = yearMatch ? +yearMatch[1] : new Date().getFullYear();
  const parts = String(str).split(/[–-]/).map((p) => p.trim());
  const parseOne = (p) => {
    const m = p.match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,})/);
    if (!m) return null;
    const month = MONTHS[m[2].slice(0, 3).toLowerCase()];
    if (month == null) return null;
    const d = new Date(Date.UTC(year, month, +m[1]));
    return isNaN(d) ? null : d.toISOString();
  };
  const start = parts[0] ? parseOne(parts[0]) : null;
  const end = parts[1] ? parseOne(parts[1]) : null;
  const out = {};
  if (start) out.start = start;
  if (end) out.end = end;
  return out;
}

/** Extract a 6-digit Indian PIN code from an address string. */
function extractPostalCode(addr) {
  const m = String(addr || '').match(/\b(\d{6})\b/);
  return m ? m[1] : undefined;
}

/** Detect an Indian state name inside an address string. */
function extractRegion(addr) {
  const s = String(addr || '');
  return INDIAN_STATES.find((st) => s.includes(st));
}

/* ── generic builder ─────────────────────────────────────────────────────── */

/**
 * @param {object} d normalized descriptor:
 *   { title, descriptionHtml, datePosted, validThrough,
 *     companyName, companyLogo, companyUrl,
 *     city, region, postalCode, country='IN',
 *     salaryMin, salaryMax, salaryUnit='YEAR', currency='INR',
 *     employmentType, directApply, identifierValue, identifierName, remote }
 * @returns {object|null} JobPosting schema, or null if required data is missing.
 */
export function buildJobPostingSchema(d = {}) {
  // Required by Google: title, description, datePosted, hiringOrganization, jobLocation.
  if (!d.title || !d.descriptionHtml || !d.datePosted || !d.companyName) return null;
  if (!d.city && !d.country) return null;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: d.title,
    description: d.descriptionHtml,
    datePosted: d.datePosted,
    hiringOrganization: {
      '@type': 'Organization',
      name: d.companyName,
      ...(d.companyUrl ? { sameAs: d.companyUrl } : {}),
      ...(d.companyLogo ? { logo: d.companyLogo } : {}),
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        ...(d.city ? { addressLocality: d.city } : {}),
        ...(d.region ? { addressRegion: d.region } : {}),
        ...(d.postalCode ? { postalCode: d.postalCode } : {}),
        addressCountry: d.country || 'IN',
      },
    },
    // Always normalize → guaranteed-valid enum (never emits raw source text).
    employmentType: mapEmploymentType(d.employmentType),
  };

  // Recommended fields — omit entirely when absent (never emit null).
  if (d.validThrough) schema.validThrough = d.validThrough;

  const hasMin = d.salaryMin != null && !Number.isNaN(d.salaryMin);
  const hasMax = d.salaryMax != null && !Number.isNaN(d.salaryMax);
  if (hasMin || hasMax) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: d.currency || 'INR',
      value: {
        '@type': 'QuantitativeValue',
        ...(hasMin ? { minValue: d.salaryMin } : {}),
        ...(hasMax ? { maxValue: d.salaryMax } : {}),
        unitText: d.salaryUnit || 'YEAR',
      },
    };
  }

  if (typeof d.directApply === 'boolean') schema.directApply = d.directApply;

  if (d.identifierValue) {
    schema.identifier = {
      '@type': 'PropertyValue',
      name: d.identifierName || d.companyName,
      value: String(d.identifierValue),
    };
  }

  // Remote roles: TELECOMMUTE + country-level applicant requirement.
  if (d.remote) {
    schema.jobLocationType = 'TELECOMMUTE';
    schema.applicantLocationRequirements = { '@type': 'Country', name: 'India' };
  }

  return schema;
}

/* ── walk-in adapter ─────────────────────────────────────────────────────── */

/** Derive a display city for a walk-in drive (mirrors the list page logic). */
export function deriveDriveCity(drive) {
  if (drive.city && drive.city.trim()) return drive.city.trim();
  if (drive.venue) return drive.venue.split(',').pop().trim();
  return '';
}

/** Compose an HTML description from the same sections the page renders. */
function composeWalkinDescription(drive) {
  const blocks = [];
  if (drive.overview?.length) blocks.push(textToHtml(drive.overview.join('\n\n')));
  if (drive.rolesOpen?.length) {
    blocks.push(`<p><strong>Roles Open</strong></p>${listToHtml(drive.rolesOpen)}`);
  }
  (drive.responsibilities || []).forEach((g) => {
    if (g?.items?.length) blocks.push(`<p><strong>${escapeHtml(g.title)}</strong></p>${listToHtml(g.items)}`);
  });
  if (drive.requirements?.length) {
    blocks.push(`<p><strong>Requirements</strong></p>${listToHtml(drive.requirements)}`);
  }
  if (drive.perks?.length) {
    blocks.push(`<p><strong>Perks &amp; Benefits</strong></p>${listToHtml(drive.perks)}`);
  }
  if (drive.keySkills?.length) {
    blocks.push(`<p><strong>Key Skills</strong></p>${listToHtml(drive.keySkills)}`);
  }
  if (drive.about) blocks.push(`<p><strong>About ${escapeHtml(drive.company || 'the company')}</strong></p>${textToHtml(drive.about)}`);
  return blocks.join('');
}

/** Rough visible-text length of composed HTML, to gate trivial descriptions. */
function htmlTextLength(html) {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length;
}

/** Map a walk-in `drive` object into the generic JobPosting descriptor. */
export function walkinToJobPostingDescriptor(drive) {
  if (!drive) return {};

  const legacyDates = parseDriveDates(drive.timeAndVenue?.dates);
  const datePosted = drive.createdAt || drive.startDate || legacyDates.start;
  const validThrough = drive.endDate || legacyDates.end;

  // Salary: prefer numeric LPA bounds, else parse the display string.
  let min = drive.salaryMinLpa != null ? Number(drive.salaryMinLpa) : undefined;
  let max = drive.salaryMaxLpa != null ? Number(drive.salaryMaxLpa) : undefined;
  if (min == null && max == null) {
    const parsed = parseSalaryLpa(drive.salary);
    min = parsed.min;
    max = parsed.max;
  }
  const address = drive.timeAndVenue?.place || drive.venue || '';

  // A trivial one-line description hurts eligibility — omit so the builder
  // returns null and the page degrades to breadcrumb-only rather than emitting
  // a thin JobPosting.
  const descriptionHtml = composeWalkinDescription(drive);
  const usableDescription = htmlTextLength(descriptionHtml) >= 120 ? descriptionHtml : '';

  return {
    title: drive.title,
    descriptionHtml: usableDescription,
    datePosted,
    validThrough,
    companyName: drive.company,
    city: deriveDriveCity(drive),
    region: extractRegion(address),
    postalCode: extractPostalCode(address),
    country: 'IN',
    salaryMin: min != null ? Math.round(min * 100000) : undefined, // LPA → ₹/year
    salaryMax: max != null ? Math.round(max * 100000) : undefined,
    salaryUnit: 'YEAR',
    currency: 'INR',
    employmentType: mapEmploymentType(drive.meta?.employmentType),
    // Walk-ins are attended physically (most link out to naukri.com); there is
    // no on-site online application, so directApply must be false (Google policy).
    directApply: false,
    identifierValue: drive.id || drive.slug,
    identifierName: drive.company,
  };
}

/* ── breadcrumbs ─────────────────────────────────────────────────────────── */

/**
 * Build a BreadcrumbList from [{ name, path }] items. `path` is relative
 * (e.g. "/walkin-drives"); the last item may omit path (current page).
 */
export function buildBreadcrumbList(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      ...(it.path ? { item: `${SITE}${it.path}` } : {}),
    })),
  };
}

/** Today's date as YYYY-MM-DD in Asia/Kolkata (IST). */
export function istToday() {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

/** Reduce an ISO string / Date / date-only string to a YYYY-MM-DD day. */
export function toDay(value) {
  if (!value) return '';
  if (value instanceof Date) return isNaN(value) ? '' : value.toISOString().slice(0, 10);
  return String(value).slice(0, 10);
}

/**
 * True when a walk-in drive is expired/closed (Phase 4 lifecycle).
 *
 * Compared at DAY granularity in Asia/Kolkata so a drive ending "today" stays
 * live through the end of the IST day (no naive-UTC off-by-one that would drop
 * still-live drives). Dateless drives are treated as NOT expired (they degrade
 * to a plain listing with no JobPosting — see item 8). The identical rule is
 * mirrored in scripts/generate-sitemap.mjs.
 */
export function isDriveExpired(drive) {
  if (!drive) return false;
  const status = String(drive.status || '').toLowerCase();
  if (status === 'expired' || status === 'closed') return true;
  const endDay = toDay(drive.endDate || parseDriveDates(drive.timeAndVenue?.dates).end);
  if (!endDay) return false; // no end date → not expired
  return endDay < istToday();
}
